import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect } from "react";
import { useNotificationById } from "@/hooks/useNotificationById";

type HumhubObjectModel = "humhub\\modules\\post\\models\\Post" | string;

interface SourceObjectDetails {
  objectModel?: HumhubObjectModel;
  objectId: number;
}

const HUMHUB_POST_MODEL = "humhub\\modules\\post\\models\\Post";
const HUMHUB_COMMENT_MODEL = "humhub\\modules\\comment\\models\\Comment";
const HUMHUB_CALENDAR_MODEL =
  "humhub\\modules\\calendar\\models\\CalendarEntry";

const getSourceObjectDetails = (
  source: unknown,
): SourceObjectDetails | null => {
  if (!source || typeof source !== "object") {
    return null;
  }

  const record = source as Record<string, unknown>;

  const rawModel = record.objectModel ?? record.object_model;
  const objectModel = typeof rawModel === "string" ? rawModel : null;

  const rawId = record.objectId ?? record.object_id ?? record.id;
  const parsedId =
    typeof rawId === "number"
      ? rawId
      : typeof rawId === "string"
        ? Number.parseInt(rawId, 10)
        : null;

  if (parsedId == null || Number.isNaN(parsedId)) {
    return null;
  }

  return {
    objectModel: objectModel ?? undefined,
    objectId: parsedId,
  };
};

// NotificationEntryComponent handles the redirection logic
const NotificationEntryComponent = () => {
  const navigate = useNavigate();

  // 直接URLSearchParamsを使用
  const urlParams = new URLSearchParams(window.location.search);
  const urlId = urlParams.get("id");

  // 直接取得したIDを使用
  const notificationId = urlId ? Number.parseInt(urlId, 10) : undefined;

  const {
    data: notification,
    isLoading,
    error,
  } = useNotificationById(notificationId || 0);

  useEffect(() => {
    if (isLoading || !notification) {
      return;
    }

    if (!notification.space_id) {
      console.warn("Notification is missing space information", notification);
      return;
    }

    const { space_id: spaceId, source_type: sourceType } = notification;

    if (sourceType === HUMHUB_POST_MODEL) {
      const sourceDetails = getSourceObjectDetails(notification.source);
      const targetPostId = sourceDetails?.objectId;

      if (targetPostId != null) {
        navigate({ to: `/study-group/${spaceId}/post/${targetPostId}` });
        return;
      }

      console.warn(
        "Unable to determine post for post notification",
        notification.source,
      );
      navigate({ to: `/study-group/${spaceId}/stream` });
      return;
    }

    if (sourceType === HUMHUB_CALENDAR_MODEL) {
      const sourceDetails = getSourceObjectDetails(notification.source);
      const targetEventId = sourceDetails?.objectId;

      if (targetEventId != null) {
        navigate({
          to: "/event-search/$event-id",
          params: { "event-id": String(targetEventId) },
          search: {
            from: `/study-group/${spaceId}/events`,
          },
        });
        return;
      }

      console.warn(
        "Unable to determine event for calendar notification",
        notification.source,
      );
      navigate({ to: `/study-group/${spaceId}/events` });
      return;
    }

    if (sourceType === HUMHUB_COMMENT_MODEL) {
      const sourceDetails = getSourceObjectDetails(notification.source);

      if (sourceDetails?.objectModel === HUMHUB_POST_MODEL) {
        navigate({
          to: `/study-group/${spaceId}/post/${sourceDetails.objectId}`,
        });
        return;
      }

      console.warn(
        "Unable to determine post for comment notification",
        notification.source,
      );
      navigate({ to: `/study-group/${spaceId}/stream` });
      return;
    }

    navigate({ to: `/study-group/${spaceId}/stream` });
  }, [notification, isLoading, navigate]);

  // Simple loading state while we process the notification
  if (isLoading) {
    return <div>Loading...</div>;
  }

  if (error) {
    return <div>Error: {error}</div>;
  }

  if (!notification) {
    return <div>Notification details unavailable.</div>;
  }

  // This will show briefly before redirect happens
  return <div>Processing notification...</div>;
};

export const Route = createFileRoute("/notification/entry")({
  component: NotificationEntryComponent,
  validateSearch: (search: Record<string, unknown>) => {
    return {
      id: search.id || "",
    };
  },
});
